import { useEffect } from 'react';
import { fetchMe } from '@/api/auth';
import { refreshTokensNow } from '@/api/apiClient';
import { queryClient } from '@/app/queryClient';
import { authStore, useAuthStore } from '@/auth/authStore';

/**
 * Brings a signed-in session up to date with what the server now grants. Roles and their
 * permissions are reconciled on the server at start-up and edited by administrators; a person
 * signed in before that carries the old list in their profile and the old claims in their token.
 * On load the profile is refreshed and, when the permissions changed, the tokens are re-issued so
 * the server sees the same claims the client now offers; every cached query is then re-asked.
 */
export function useProfileSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      const before = authStore.getState().user?.permissions ?? [];
      try {
        const me = await fetchMe();
        if (cancelled) return;
        const after = me.permissions ?? [];
        const changed = before.length !== after.length || before.some((p) => !after.includes(p)) || after.some((p) => !before.includes(p));
        if (changed) {
          await refreshTokensNow();
          void queryClient.invalidateQueries();
        }
      } catch {
        // The session carries on with what it has; the next load tries again.
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);
}
